import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import socketClient from '../utils/socketClient';
import LogoLoader from '../components/Common/LogoLoader';
import { useTranslation } from 'react-i18next';
import PlatformPageHeader from '../components/Common/PlatformPageHeader';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { Avatar, Box, Button, Container, List, ListItemButton, Paper, Stack, TextField, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';

export default function ChatPage() {
  const { isAuthenticated, user } = useAuth();
  const { t } = useTranslation();
  const [contacts, setContacts] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const activeRef = useRef(null);
  const messagesEndRef = useRef(null);

  const loadContacts = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const res = await api.get('/api/second-hand-offers/chat-contacts');
      const list = res.data?.data || [];
      const flattened = list.flatMap((contact) => {
        const offers = contact.offers || [];
        if (offers.length === 0) {
          return [{
            id: `${contact.userId}-general`,
            userId: contact.userId,
            username: contact.username,
            email: contact.email,
            productId: null,
            productName: t('chat.generalChat'),
            offerAmount: null,
            acceptedAt: null,
          }];
        }
        return offers.map((offer) => ({
          id: `${contact.userId}-${offer.product?.id || offer.offerId}`,
          userId: contact.userId,
          username: contact.username,
          email: contact.email,
          productId: offer.product?.id || null,
          productName: offer.product?.name || t('chat.unknownProduct'),
          offerAmount: offer.offerAmount || null,
          acceptedAt: offer.acceptedAt || null,
        }));
      });
      setContacts(flattened);
    } catch (e) {
      console.error('Failed to load contacts', e);
    }
  }, [isAuthenticated]);

  const normalizeId = useCallback((value) => {
    if (!value) return null;
    if (typeof value === 'string') return value;
    if (typeof value === 'object') {
      if (value.$oid) return value.$oid;
      if (value._id) return normalizeId(value._id);
      if (typeof value.toString === 'function' && value.toString !== Object.prototype.toString) {
        return value.toString();
      }
    }
    return String(value);
  }, []);

  const loadMessages = useCallback(async ({ partnerId, productId }) => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const normalizedProductId = normalizeId(productId);
      const params = new URLSearchParams({ partnerId });
      if (normalizedProductId) {
        params.append('productId', normalizedProductId);
      }

      const res = await api.get(`/api/messages?${params.toString()}`);
      const list = res.data?.data || [];
      const filtered = list.filter((msg) => {
        if (!normalizedProductId) return true;
        const relatedId = normalizeId(msg.relatedId);
        return relatedId === normalizedProductId;
      });
      setMessages(
        filtered.sort((a, b) => new Date(a.timestamp || a.createdAt) - new Date(b.timestamp || b.createdAt))
      );
    } catch (e) {
      console.error('Failed to load messages', e);
    } finally {
      setLoading(false);
    }
  }, [normalizeId]);

  const sendMessage = async () => {
    if (!active || !input.trim()) return;
    const content = input.trim();
    try {
      setInput('');
      const normalizedProductId = normalizeId(active.productId);

      if (socketClient?.isConnected && socketClient?.isAuthenticated) {
        socketClient.sendChatMessage(active.userId, normalizedProductId, content);
      } else {
        const payload = { receiverId: active.userId, content };
        if (normalizedProductId) {
          payload.productId = normalizedProductId;
        }
        await api.post('/api/messages', payload);
        await loadMessages({ partnerId: active.userId, productId: normalizedProductId });
      }
    } catch (e) {
      console.error('Failed to send message', e);
    }
  };

  useEffect(() => { loadContacts(); }, [loadContacts]);

  useEffect(() => {
    if (!active) return;
    activeRef.current = active;
    const normalizedProductId = normalizeId(active.productId);
    loadMessages({ partnerId: active.userId, productId: normalizedProductId });

    if (socketClient?.isConnected && socketClient?.isAuthenticated) {
      socketClient.markMessagesAsRead(normalizedProductId, active.userId);
      if (normalizedProductId) {
        socketClient.joinOfferRoom(normalizedProductId);
        return () => socketClient.leaveOfferRoom(normalizedProductId);
      }
      return undefined;
    }

    // Fallback to HTTP mark-read when socket not ready
    (async () => {
      try {
        if (!normalizedProductId) return;
        await api.put('/api/messages/mark-read', {
          partnerId: active.userId,
          productId: normalizedProductId,
        });
      } catch (err) {
        console.error('Failed to mark messages read via API', err);
      }
    })();

    return undefined;
  }, [active, loadMessages, normalizeId]);

  useEffect(() => {
    const handleChatMessage = ({ message, productId }) => {
      const current = activeRef.current;
      if (!current) return;

      const senderId =
        message.senderId?._id?.toString() ||
        message.senderId?._id ||
        message.senderId?.toString?.() ||
        message.senderId;
      const receiverId =
        message.receiverId?._id?.toString() ||
        message.receiverId?._id ||
        message.receiverId?.toString?.() ||
        message.receiverId;
    const partnerId = current.userId?.toString();
    const matchesPartner = senderId === partnerId || receiverId === partnerId;
    const normalizedCurrentProduct = normalizeId(current.productId);
    const normalizedIncomingProduct = normalizeId(productId);
    const matchesProduct = normalizedCurrentProduct ? normalizedIncomingProduct === normalizedCurrentProduct : true;

      if (!matchesPartner || !matchesProduct) return;

      setMessages((prev) => {
        if (prev.some((m) => m._id === message._id)) return prev;
        const next = [...prev, message];
        return next.sort((a, b) => new Date(a.timestamp || a.createdAt) - new Date(b.timestamp || b.createdAt));
      });

      const currentUserId = user?._id?.toString();
      if (socketClient?.isConnected && socketClient?.isAuthenticated) {
        if (senderId !== currentUserId) {
          socketClient.markMessagesAsRead(normalizedCurrentProduct, current.userId);
        }
      }
    };

    const handleMessagesRead = ({ productId, readBy }) => {
      const current = activeRef.current;
      if (!current) return;
    const normalizedCurrentProduct = normalizeId(current.productId);
    const normalizedIncomingProduct = normalizeId(productId);
    const matchesProduct = normalizedCurrentProduct ? normalizedIncomingProduct === normalizedCurrentProduct : true;
      if (!matchesProduct) return;

      setMessages((prev) =>
        prev.map((msg) => {
          const receiverId = normalizeId(msg.receiverId);
          if (receiverId === normalizeId(readBy)) {
            return { ...msg, status: 'read' };
          }
          return msg;
        })
      );
    };

    socketClient.on('chat_message', handleChatMessage);
    socketClient.on('messages_read', handleMessagesRead);

    return () => {
      socketClient.off('chat_message', handleChatMessage);
      socketClient.off('messages_read', handleMessagesRead);
    };
  }, [normalizeId, user?._id]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    if (!active && contacts.length > 0) {
      setActive(contacts[0]);
    }
  }, [contacts, active]);

  if (!isAuthenticated) return null;

  return (
    <SciFiBackground>
      <Navbar />
      <Box component="main" sx={{ minHeight: 'calc(100vh - 72px)', py: { xs: 4, md: 7 }, px: 2 }}>
        <Container maxWidth="xl">
          <PlatformPageHeader
            icon={ForumRoundedIcon}
            eyebrow="VITRIA / CONVERSATIONS"
            title={t('chat.contacts')}
            subtitle={t('chat.selectContact')}
            sx={{ mb: { xs: 3.5, md: 4.5 } }}
          />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '330px minmax(0, 1fr)' }, gap: 2 }}>
            <Paper sx={{ height: { md: '72vh' }, minHeight: 540, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ px: 2.5, py: 2.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="overline" color="text.secondary">{t('chat.contacts')}</Typography>
                <Typography variant="h6">{contacts.length} {t('chat.contacts').toLowerCase()}</Typography>
              </Box>
              <List sx={{ p: 1.25, maxHeight: 'calc(72vh - 86px)', overflowY: 'auto' }}>
                {contacts.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>{t('chat.noChatsYet')}</Typography>
                )}
                {contacts.map((contact) => (
                  <ListItemButton
                    key={contact.id}
                    selected={active?.id === contact.id}
                    onClick={() => setActive(contact)}
                    sx={{ alignItems: 'flex-start', gap: 1.5, mb: 0.75, p: 1.5 }}
                  >
                    <Avatar sx={{ width: 39, height: 39, fontSize: '.88rem' }}>{contact.username?.[0]?.toUpperCase() || 'V'}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={720} noWrap>{contact.username}</Typography>
                      <Typography variant="caption" color="text.secondary" noWrap display="block">{contact.email}</Typography>
                      {contact.productName && (
                        <Typography variant="caption" color="primary.light" noWrap display="block">
                          {t('chat.product', { name: contact.productName })}
                        </Typography>
                      )}
                    </Box>
                  </ListItemButton>
                ))}
              </List>
            </Paper>

            <Paper sx={{ display: 'flex', height: { md: '72vh' }, minHeight: 540, flexDirection: 'column', overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
              {active ? (
                <>
                  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: { xs: 2, sm: 3 }, py: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Avatar sx={{ width: 42, height: 42 }}>{active.username?.[0]?.toUpperCase() || 'V'}</Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" noWrap>{active.username}</Typography>
                      {active.productName && <Typography variant="caption" color="text.secondary">{t('chat.chatAbout', { name: active.productName })}</Typography>}
                    </Box>
                  </Stack>

                  <Stack spacing={1.25} sx={{ flex: 1, overflowY: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>
                    {loading ? (
                      <Box sx={{ display: 'grid', flex: 1, placeItems: 'center' }}><LogoLoader size={60} /></Box>
                    ) : messages.length === 0 ? (
                      <Box sx={{ display: 'grid', flex: 1, placeItems: 'center', textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">{t('chat.noMessages')}</Typography>
                      </Box>
                    ) : (
                      messages.map((message) => {
                        const mine = message.senderId?._id?.toString() === user?._id?.toString()
                          || message.senderId === user?._id
                          || message.senderId?.toString?.() === user?._id?.toString();
                        return (
                          <Box key={message._id} sx={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: { xs: '88%', sm: '72%' } }}>
                            <Box
                              sx={(theme) => ({
                                px: 2,
                                py: 1.25,
                                border: '1px solid',
                                borderColor: mine ? alpha(theme.palette.primary.main, .3) : 'divider',
                                borderRadius: mine ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                bgcolor: mine ? alpha(theme.palette.primary.main, .15) : theme.vitria?.input,
                              })}
                            >
                              <Typography variant="body2">{message.content}</Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: .45, px: .5, textAlign: mine ? 'right' : 'left' }}>
                              {mine ? t('chat.you') : (message.senderId?.username || t('chat.partner'))}
                            </Typography>
                          </Box>
                        );
                      })
                    )}
                    <div ref={messagesEndRef} />
                  </Stack>

                  <Stack direction="row" spacing={1.25} sx={{ p: { xs: 1.5, sm: 2 }, borderTop: '1px solid', borderColor: 'divider' }}>
                    <TextField
                      fullWidth
                      size="small"
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); sendMessage(); } }}
                      placeholder={t('chat.typePlaceholder')}
                    />
                    <Button variant="contained" onClick={sendMessage} endIcon={<SendRoundedIcon />} sx={{ px: { xs: 2, sm: 3 } }}>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{t('chat.send')}</Box>
                    </Button>
                  </Stack>
                </>
              ) : (
                <Box sx={{ display: 'grid', flex: 1, placeItems: 'center', p: 4, textAlign: 'center' }}>
                  <Box>
                    <ForumRoundedIcon sx={{ mb: 2, color: 'primary.light', fontSize: 48 }} />
                    <Typography variant="h6">{t('chat.selectContact')}</Typography>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Container>
      </Box>
    </SciFiBackground>
  );
}
